import { useEffect, useState } from 'react';
import { Checkbox, Select } from 'antd';
import {
  DelayNotifyChannel,
  IntervalUnit,
  type DelayIfNothingHappens,
  type DelayUntilEvent,
  type DelayWaitEvent,
} from '@/proto/logiblocks/logiblocks';
import { CustomSelectIcon, DeleteIcon } from '@/assets/icons/icons';
import AddButton from '@/components/AddButton';
import CustomDeleteModal from '@/pages/Modals/CustomDeleteModal';
import type { ValidationError } from '@/pages/Logiblocks/fieldValidation';
import {
  durationToSeconds,
  secondsToDuration,
} from '@/pages/Logiblocks/dateTimeUtils';
import { FieldErrorLabel, FieldLabel } from '../shared';
import customizationStyles from '../NodeCustomization.module.css';
import { DelayEventCard } from './DelayEventCard';
import { DelayNotifyRecipients } from './DelayNotifyRecipients';
import { useReplyChannels } from './DelayReplyToSelect';
import {
  delayEventField,
  DelayUntilEventFields,
  EVENT_KIND_META,
  eventDetailText,
  eventSummaryText,
  getEventKind,
  MSG_DEADLINE_HELP,
  NOTIFY_CHANNEL_OPTIONS,
} from './delayUntilEventModel';
import { useScrollToFirstError } from './useScrollToFirstError';
import styles from './DelayUntilEvent.module.css';

/**
 * What typed deadline text means in seconds: nothing for an empty field, and 0 (always invalid)
 * for text that is not a plain number.
 */
const deadlineSeconds = (
  text: string,
  unit: IntervalUnit
): number | undefined => {
  const trimmed = text.trim();
  if (trimmed === '') return undefined;
  const amount = Number(trimmed);
  return Number.isFinite(amount) ? durationToSeconds(amount, unit) : 0;
};

/**
 * The list's wording for "Notify automation owner": the owner's address, as the PRD's copy has it.
 * An owner with no address cannot be emailed, so the label says so instead of promising one.
 */
const ownerLabel = (ownerEmail: string | null | undefined): string => {
  if (ownerEmail === undefined) return 'Notify automation owner';
  return ownerEmail
    ? `Notify automation owner (${ownerEmail})`
    : 'Notify automation owner (no email on file)';
};

interface DelayUntilEventEditorProps {
  nodeId: string;
  untilEvent: DelayUntilEvent;
  onChange: (untilEvent: DelayUntilEvent) => void;
  /** "+ Add event": opens the Add event screen. */
  onAddEvent: () => void;
  /** A card or its chevron: opens that event's settings screen. */
  onOpenEvent: (event: DelayWaitEvent) => void;
  isReadOnly: boolean;
  /**
   * The automation owner's email, shown on "Notify automation owner". Null when the owner has
   * no address, undefined when the editor was not given one.
   */
  ownerEmail?: string | null;
  validationErrors?: ValidationError[];
}

/** The text after the event name for a card, reading the graph for reply events. */
const EventDetailText = ({
  nodeId,
  event,
}: {
  nodeId: string;
  event: DelayWaitEvent;
}) =>
  event.reply_received ? (
    <ReplyDetailText nodeId={nodeId} event={event} />
  ) : (
    <>{eventDetailText(event)}</>
  );

const ReplyDetailText = ({
  nodeId,
  event,
}: {
  nodeId: string;
  event: DelayWaitEvent;
}) => {
  const channels = useReplyChannels(
    nodeId,
    event.reply_received ?? { source_node_ids: [] }
  );
  return <>{eventDetailText(event, channels)}</>;
};

const NOTHING_HAPPENS = {
  END_RUN: 'end_run',
  NOTIFY_OWNER: 'notify_owner',
  NOTIFY_OTHERS: 'notify_others',
} as const;

/**
 * The Delay panel's "Delay until event" form: the event cards, the deadline
 * and what happens when the deadline passes. Adding and editing an event
 * happen on their own panel screens, opened through `onAddEvent` and
 * `onOpenEvent`.
 */
export const DelayUntilEventEditor = ({
  nodeId,
  untilEvent,
  onChange,
  onAddEvent,
  onOpenEvent,
  isReadOnly,
  validationErrors,
  ownerEmail,
}: DelayUntilEventEditorProps) => {
  const errorOf = (field: string) =>
    validationErrors?.find((e) => e.field === field);
  const errorScopeRef = useScrollToFirstError<HTMLDivElement>(
    !!validationErrors?.length
  );

  const deleteEvent = (eventId: string) =>
    onChange({
      ...untilEvent,
      events: untilEvent.events.filter((e) => e.id !== eventId),
    });
  // The card the author asked to delete and has not yet confirmed. Nothing is checked first: the
  // dialog is asked for every event, so what it says has to hold whether or not anything later in
  // the workflow relies on it.
  const [pendingDelete, setPendingDelete] = useState<DelayWaitEvent | null>(
    null
  );

  // --- Stop waiting after ------------------------------------------------------

  const deadline = untilEvent.stop_waiting_after;
  // The unit lives here too so it can change while the field is empty (no
  // deadline stored) without storing a deadline of 0.
  const [deadlineUnit, setDeadlineUnit] = useState(
    deadline?.display_unit ?? IntervalUnit.HOURS
  );
  const [deadlineText, setDeadlineText] = useState(() =>
    deadline
      ? String(secondsToDuration(deadline.seconds, deadline.display_unit))
      : ''
  );

  // Follow outside changes (such as switching nodes) without disturbing text
  // that already means the stored value, such as a half-typed "1." or "abc".
  useEffect(() => {
    if (!deadline) {
      setDeadlineText('');
      return;
    }
    setDeadlineUnit(deadline.display_unit);
    setDeadlineText((text) =>
      deadlineSeconds(text, deadline.display_unit) === deadline.seconds
        ? text
        : String(secondsToDuration(deadline.seconds, deadline.display_unit))
    );
  }, [deadline]);

  // A free text field: anything can be typed. Text that is not a plain number is stored as 0, which
  // the field reports as an error and which blocks Save, instead of being silently ignored.
  const handleDeadlineChange = (text: string) => {
    setDeadlineText(text);
    const seconds = deadlineSeconds(text, deadlineUnit);
    onChange({
      ...untilEvent,
      stop_waiting_after:
        seconds === undefined
          ? undefined
          : { seconds, display_unit: deadlineUnit },
    });
  };

  // One click switches hr and min, keeping the number shown.
  const handleDeadlineUnitToggle = () => {
    const nextUnit =
      deadlineUnit === IntervalUnit.HOURS
        ? IntervalUnit.MINUTES
        : IntervalUnit.HOURS;
    setDeadlineUnit(nextUnit);
    const seconds = deadlineSeconds(deadlineText, nextUnit);
    if (seconds === undefined) return;
    onChange({
      ...untilEvent,
      stop_waiting_after: { seconds, display_unit: nextUnit },
    });
  };

  // --- If nothing happens ------------------------------------------------------

  const nothingHappens = untilEvent.if_nothing_happens;
  const notifyOthers = nothingHappens?.notify_others;

  const updateNothingHappens = (patch: Partial<DelayIfNothingHappens>) =>
    onChange({
      ...untilEvent,
      if_nothing_happens: {
        end_run: nothingHappens?.end_run ?? false,
        notify_automation_owner:
          nothingHappens?.notify_automation_owner ?? false,
        notify_others: notifyOthers,
        ...patch,
      },
    });

  const nothingHappensValue: string[] = [
    ...(nothingHappens?.end_run ? [NOTHING_HAPPENS.END_RUN] : []),
    ...(nothingHappens?.notify_automation_owner
      ? [NOTHING_HAPPENS.NOTIFY_OWNER]
      : []),
    ...(notifyOthers ? [NOTHING_HAPPENS.NOTIFY_OTHERS] : []),
  ];

  // The list shows the full wording; a chip in the select shows the short one.
  const nothingHappensOptions = [
    {
      value: NOTHING_HAPPENS.END_RUN,
      label: 'End the run',
      chip: 'End the run',
    },
    {
      value: NOTHING_HAPPENS.NOTIFY_OWNER,
      label: ownerLabel(ownerEmail),
      chip: 'Notify automation owner',
    },
    {
      value: NOTHING_HAPPENS.NOTIFY_OTHERS,
      label: 'Notify someone else',
      chip: 'Notify someone else',
    },
  ];

  const handleNothingHappensChange = (values: string[]) =>
    updateNothingHappens({
      end_run: values.includes(NOTHING_HAPPENS.END_RUN),
      notify_automation_owner: values.includes(NOTHING_HAPPENS.NOTIFY_OWNER),
      notify_others: values.includes(NOTHING_HAPPENS.NOTIFY_OTHERS)
        ? // Email is picked when "Notify someone else" is first chosen.
          (notifyOthers ?? {
            channels: [DelayNotifyChannel.DELAY_NOTIFY_EMAIL],
            recipients: [],
          })
        : undefined,
    });

  // One channel at a time (PRD 5.1). The saved field is still a list, so a pick replaces it.
  const chooseChannel = (channel: DelayNotifyChannel) => {
    if (!notifyOthers) return;
    updateNothingHappens({
      notify_others: { ...notifyOthers, channels: [channel] },
    });
  };

  // --- Render ------------------------------------------------------------------

  // The product's own add pill (as "Add condition" in the Conditional node), so it hovers like
  // the others. The wrapper takes the click (a click on the pill bubbles up to it) and makes the
  // pill a keyboard-reachable button, which it is not on its own.
  const addEventPill = !isReadOnly && (
    <div
      role='button'
      tabIndex={0}
      aria-label='Add event'
      className={styles.addEventPill}
      onClick={onAddEvent}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onAddEvent();
        }
      }}
    >
      <AddButton addButtonWidth={82} text='Add event' padding='6px 12px' />
    </div>
  );

  return (
    <div ref={errorScopeRef} className={styles.errorScope}>
      <CustomDeleteModal
        visible={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) deleteEvent(pendingDelete.id);
          setPendingDelete(null);
        }}
        title={
          pendingDelete ? `Delete “${eventSummaryText(pendingDelete)}”?` : ''
        }
        message='This event will be removed from this Delay. Anything later in the workflow that relies on it may stop working.'
        cancelText='Cancel'
        confirmText='Delete event'
        confirmColor='var(--text-primary)'
        // The design's measurements: 676 wide, 20 under the title, 16 between the buttons.
        width={676}
        titleGap='20px'
        actionsGap={16}
      />
      <div className={customizationStyles.customizationSection}>
        {untilEvent.events.length > 0 && (
          <FieldLabel required className={customizationStyles.sectionLabel}>
            Continue when any of these events happens
          </FieldLabel>
        )}
        {untilEvent.events.map((event) => {
          const kind = getEventKind(event);
          const cardError = errorOf(delayEventField(event.id));
          return (
            <div key={event.id} className={styles.cardRow}>
              <div className={styles.cardColumn}>
                <DelayEventCard
                  kind={kind}
                  title={kind ? EVENT_KIND_META[kind].label : 'Unknown event'}
                  subtitle={<EventDetailText nodeId={nodeId} event={event} />}
                  hasError={!!cardError}
                  ariaLabel={`Edit ${eventSummaryText(event)}`}
                  onClick={() => onOpenEvent(event)}
                />
                <FieldErrorLabel error={cardError} />
              </div>
              {!isReadOnly && (
                <button
                  type='button'
                  className={styles.trashButton}
                  aria-label={`Delete ${eventSummaryText(event)}`}
                  onClick={() => setPendingDelete(event)}
                >
                  <DeleteIcon width={20} height={20} />
                </button>
              )}
            </div>
          );
        })}
        {addEventPill}
        <FieldErrorLabel error={errorOf(DelayUntilEventFields.EVENTS)} />
      </div>

      <div className={customizationStyles.customizationSection}>
        <label className={customizationStyles.sectionLabel}>
          Stop waiting after
        </label>
        <div className={styles.deadlineField}>
          <input
            type='text'
            aria-label='Stop waiting after'
            value={deadlineText}
            onChange={(e) => handleDeadlineChange(e.target.value)}
            className={customizationStyles.lbNumberInput}
            disabled={isReadOnly}
          />
          <button
            type='button'
            className={styles.unitToggle}
            onClick={handleDeadlineUnitToggle}
            disabled={isReadOnly}
            aria-label={`Unit: ${deadlineUnit === IntervalUnit.HOURS ? 'hours' : 'minutes'}. Switch unit`}
          >
            {deadlineUnit === IntervalUnit.HOURS ? 'hr' : 'min'}
          </button>
        </div>
        <FieldErrorLabel
          error={errorOf(DelayUntilEventFields.STOP_WAITING_AFTER)}
        />
        <span className={customizationStyles.timezoneNote}>
          {MSG_DEADLINE_HELP}
        </span>
      </div>

      <div className={customizationStyles.customizationSection}>
        <label className={customizationStyles.sectionLabel}>
          If nothing happens
        </label>
        <Select
          mode='multiple'
          aria-label='If nothing happens'
          value={nothingHappensValue}
          onChange={handleNothingHappensChange}
          options={nothingHappensOptions}
          optionLabelProp='chip'
          optionRender={(option) => (
            <Checkbox
              checked={nothingHappensValue.includes(String(option.value))}
            >
              {option.label}
            </Checkbox>
          )}
          menuItemSelectedIcon={null}
          // The design's chevron, which a multi-select also keeps while open: antd would swap in a
          // magnifier.
          suffixIcon={<CustomSelectIcon />}
          disabled={isReadOnly}
          placeholder='Select'
          className={`agentSelect ${styles.nothingHappensSelect} ${styles.multiSelect}`}
          style={{ width: '100%' }}
        />
      </div>

      {notifyOthers && (
        <>
          <div className={customizationStyles.customizationSection}>
            <FieldLabel required className={customizationStyles.sectionLabel}>
              Notify via
            </FieldLabel>
            <div
              className={styles.chips}
              role='radiogroup'
              aria-label='Notify via'
            >
              {NOTIFY_CHANNEL_OPTIONS.map((option) => {
                const picked = notifyOthers.channels.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type='button'
                    role='radio'
                    aria-checked={picked}
                    disabled={isReadOnly}
                    className={`${styles.chip} ${picked ? styles.chipPicked : ''}`}
                    onClick={() => chooseChannel(option.value)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <FieldErrorLabel
              error={errorOf(DelayUntilEventFields.NOTIFY_VIA)}
            />
          </div>
          <div className={customizationStyles.customizationSection}>
            <FieldLabel required className={customizationStyles.sectionLabel}>
              Recipients
            </FieldLabel>
            <DelayNotifyRecipients
              value={notifyOthers.recipients}
              disabled={isReadOnly}
              onChange={(recipients) =>
                updateNothingHappens({
                  notify_others: { ...notifyOthers, recipients },
                })
              }
            />
            <FieldErrorLabel
              error={errorOf(DelayUntilEventFields.RECIPIENTS)}
            />
          </div>
        </>
      )}
    </div>
  );
};
